import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { inr, fmtDate } from "@/api";
import LogoMark from "@/components/Logo";
import { usePlatformName } from "@/brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, FileText } from "lucide-react";

export default function PublicQuote() {
  const { token } = useParams();
  const platform = usePlatformName();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get(`/public/quotation/${token}`).then((r) => setQ(r.data)).catch((e) => setErr(e.response?.data?.detail || "Quotation not found"));
  }, [token]);

  const respond = async (action) => {
    try {
      const r = await api.post(`/public/quotation/${token}/respond`, { action, note });
      setDone(r.data.status);
      toast.success(`Quotation ${r.data.status.toLowerCase()} — thank you!`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-primary-tint p-6">
      <div className="max-w-md w-full bg-surface rounded-2xl border p-8 text-center" data-testid="public-quote-error">
        <XCircle className="mx-auto text-danger-foreground mb-3" size={32} />
        <p className="text-sm text-muted-foreground">{err}</p>
      </div>
    </div>
  );
  if (!q) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading quotation…</div>;

  const statusColor = { Accepted: "text-success", Rejected: "text-danger", Viewed: "text-info", Sent: "text-warning" };
  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="public-quote-page">
      <div className="max-w-2xl mx-auto bg-surface rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-primary text-primary-foreground p-6 flex items-center justify-between">
          <div className="flex items-center gap-3"><LogoMark size={36} /><div><div className="font-heading font-bold">{q.company}</div><div className="text-xs opacity-80">Quotation {q.quote_no}{q.revision_no > 1 ? ` · Rev ${q.revision_no}` : ""}</div></div></div>
          <FileText size={28} className="opacity-60" />
        </div>
        <div className="p-6 space-y-5">
          <div className="flex justify-between flex-wrap gap-2 text-sm">
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Customer</div><b>{q.customer_name}</b><div className="text-muted-foreground">{q.phone}</div></div>
            <div className="text-right"><div className="text-xs uppercase tracking-widest text-muted-foreground">Date</div>{fmtDate(q.created_at)}{q.validity_days ? <div className="text-xs text-muted-foreground">Valid {q.validity_days} days</div> : null}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Service</div>
            <div className="font-medium">{q.service}</div>
          </div>
          {q.items?.length > 0 && (
            <table className="w-full text-sm border rounded-lg overflow-hidden" data-testid="public-quote-items">
              <thead className="bg-primary-tint text-xs uppercase text-muted-foreground"><tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Amount</th></tr></thead>
              <tbody>
                {q.items.map((it, i) => (
                  <tr key={i} className="border-t"><td className="px-3 py-2">{it.name}</td><td className="px-3 py-2 text-right">{it.qty} {it.unit}</td><td className="px-3 py-2 text-right">{inr(it.rate)}</td><td className="px-3 py-2 text-right font-mono">{inr(it.qty * it.rate * (1 - (it.discount_pct || 0) / 100))}</td></tr>
                ))}
              </tbody>
            </table>
          )}
            <div className="bg-primary-tint rounded-xl p-4 flex justify-between items-center" data-testid="public-quote-amount">
            <div><div className="text-xs uppercase tracking-widest text-primary">Total</div><div className="text-xs text-muted-foreground">{q.amount_words}</div></div>
            <div className="font-mono text-3xl font-extrabold text-primary">{inr(q.amount)}</div>
          </div>
          {(q.scope_of_work || q.inclusions || q.exclusions || q.warranty || q.payment_terms) && (
            <div className="grid gap-3 text-sm">
              {[["Scope of Work", q.scope_of_work], ["Inclusions", q.inclusions], ["Exclusions", q.exclusions], ["Warranty", q.warranty], ["Payment Terms", q.payment_terms]].filter(([, v]) => v).map(([l, v]) => (
                <div key={l}><div className="text-xs uppercase tracking-widest text-muted-foreground">{l}</div><div className="whitespace-pre-wrap">{v}</div></div>
              ))}
            </div>
          )}
          {(done || ["Accepted", "Rejected"].includes(q.status)) ? (
            <div className={`text-center py-6 rounded-xl border-2 ${(done || q.status) === "Accepted" ? "border-success bg-success-tint" : "border-danger bg-danger-tint"}`} data-testid="public-quote-result">
              {(done || q.status) === "Accepted" ? <CheckCircle2 className="mx-auto text-success mb-2" size={36} /> : <XCircle className="mx-auto text-danger mb-2" size={36} />}
              <div className="font-heading font-bold text-lg">Quotation {(done || q.status)}</div>
              <div className="text-sm text-muted-foreground">We've notified {q.company}. Thank you!</div>
            </div>
          ) : (
            <div className="space-y-3 border-t pt-4">
              <div className="text-sm">Current status: <b className={statusColor[q.status] || ""}>{q.status}</b></div>
              <Textarea data-testid="public-quote-note" placeholder="Note for the company (optional)…" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                <Button data-testid="public-quote-accept" onClick={() => respond("accept")} className="h-12 bg-success text-inverse hover:bg-success/90 text-base"><CheckCircle2 size={17} className="mr-2" /> Accept Quotation</Button>
                <Button data-testid="public-quote-reject" onClick={() => respond("reject")} variant="outline" className="h-12 border-danger text-danger-foreground hover:bg-danger-tint text-base"><XCircle size={17} className="mr-2" /> Reject</Button>
              </div>
            </div>
          )}
        </div>
        <div className="text-center text-[11px] text-muted-foreground pb-4">Powered by {platform}</div>
      </div>
    </div>
  );
}
